import {
    useCallback,
    useEffect,
    useEffectEvent,
    useRef,
    useState,
} from "react";
import { useFormik, FormikProvider } from "formik";
import { CloseTemplateBasis } from "@common/types/closeTemplateBasis";
import type { OrganizationTaskTemplate } from "@common/types/organizationTaskTemplate";
import { TaskType } from "@common/types/task";
import { OrganizationCloseStage } from "@common/types/organizationCloseStage";
import {
    TaskTemplateAssigneeRole,
    TaskTemplateDeadlineAnchor,
} from "@common/types/taskTemplate";
import { Button } from "@design-system/Button/Button";
import { validateFormikWithZodSchema } from "@helpers/form";
import { TaskDrawerHeaderFields } from "@components/drawers/TaskDrawerHeaderFields";
import { LocalTask } from "./closeTemplateTasksDrawer.types";
import { toDrawerDefaultsFromOrgTaskTemplate } from "./orgTaskTemplatePicker.utils";
import {
    normalizeTransactionReviewViewConfigForBasis,
    parseBalanceSheetCloseTemplateViewConfig,
    parseDocumentRetrievalCloseTemplateViewConfig,
    parseProfitLossCloseTemplateViewConfig,
    readBalanceSheetMonthsFromViewConfig,
    readDocumentRetrievalEndDateOverride,
    readDocumentRetrievalFinancialAccountIds,
    readDocumentRetrievalFinancialAccountType,
    readDocumentRetrievalStartDateOverride,
    readProfitLossMonthsFromViewConfig,
    readReconciliationFinancialAccountIds,
    readReconciliationFinancialAccountTypes,
    readReviewFieldsFromViewConfig,
    readTransactionsViewTypeFromViewConfig,
    serializeBalanceSheetCloseTemplateViewConfigWithMonths,
    serializeDocumentRetrievalCloseTemplateViewConfigWithDates,
    serializeProfitLossCloseTemplateViewConfigWithMonths,
    serializeReconciliationCloseTemplateViewConfig,
    type TransactionsViewType,
    withTransactionsViewType,
} from "./closeTemplateTaskViewConfig";
import { EditTaskWorkflowFields } from "./templateTaskDrawer/EditTaskWorkflowFields";
import { LockedTaskTypePicker } from "./templateTaskDrawer/LockedTaskTypePicker";
import { TaskLinkFields } from "./templateTaskDrawer/TaskLinkFields";
import { TemplateTaskDependenciesSection } from "./templateTaskDrawer/TemplateTaskDependenciesSection";
import { TemplateTaskDetailsSection } from "./templateTaskDrawer/TemplateTaskDetailsSection";
import { TemplateTaskDrawerShell } from "./templateTaskDrawer/TemplateTaskDrawerShell";
import { TemplateTaskPreviewPane } from "./templateTaskDrawer/TemplateTaskPreviewPane";
import { TemplateTaskSections } from "./templateTaskDrawer/TemplateTaskSections";
import { TemplateTaskWorkflowSection } from "./templateTaskDrawer/TemplateTaskWorkflowSection";
import {
    TemplateTaskDetailsSchema,
    type TemplateTaskDetailsValues,
} from "./templateTaskDrawer/templateTaskDetails.schema";
import { useInitialMessageHandler } from "./templateTaskDrawer/useInitialMessageHandler";
import { useTaskLinkState } from "./templateTaskDrawer/useTaskLinkState";

export interface EditTaskResult {
    name: string;
    closeStageId: string | null;
    assigneeRole: TaskTemplateAssigneeRole | null;
    assigneeId: number | null;
    externalDeadline: number | null;
    deadlineAnchor: TaskTemplateDeadlineAnchor | null;
    estimatedTime: number | null;
    initialMessage: string | null;
    viewConfig: Record<string, unknown> | null;
    taskType: TaskType | null;
    sourceTemplateVersion: number | null;
    blockedByLocalIds?: string[];
    relatedLocalIds?: string[];
}

interface Props {
    open: boolean;
    task: LocalTask | null;
    sortedStages: OrganizationCloseStage[];
    onClose: () => void;
    onSave: (localId: string, result: EditTaskResult) => void | Promise<void>;
    basis?: CloseTemplateBasis | null;
    latestSourceTemplate?: OrganizationTaskTemplate | null;
    linkableTasks?: readonly LocalTask[];
}

export const EditCloseTemplateTaskDrawer = ({
    open,
    task,
    sortedStages,
    onClose,
    onSave,
    basis,
    latestSourceTemplate = null,
    linkableTasks,
}: Props) => {
    const links = useTaskLinkState(task, linkableTasks);
    const [taskType, setTaskType] = useState(task?.taskType ?? null);
    const [sourceTemplateVersion, setSourceTemplateVersion] = useState(
        task?.sourceTemplateVersion ?? null,
    );
    const [viewConfig, setViewConfig] = useState<Record<
        string,
        unknown
    > | null>(task?.viewConfig ?? null);
    const [transactionsViewType, setTransactionsViewType] =
        useState<TransactionsViewType>(() =>
            readTransactionsViewTypeFromViewConfig(task?.viewConfig ?? null),
        );

    const nameInputRef = useRef<HTMLInputElement>(null);
    useEffect(() => {
        if (open) {
            nameInputRef.current?.focus();
        }
    }, [open]);

    const viewConfigRef = useRef(viewConfig);
    useEffect(() => {
        viewConfigRef.current = viewConfig;
    });

    // Reset viewConfig when the drawer opens for a different task.
    // We avoid putting task.viewConfig in the dep array to prevent clobbering
    // edits when the parent re-renders while the drawer stays open.
    const resetViewConfigFromTask = useEffectEvent(() => {
        if (task?.taskType === TaskType.PROFIT_LOSS) {
            const rawPLViewConfig = task.viewConfig ?? null;
            const months = readProfitLossMonthsFromViewConfig(rawPLViewConfig);
            setViewConfig(
                serializeProfitLossCloseTemplateViewConfigWithMonths(
                    parseProfitLossCloseTemplateViewConfig(rawPLViewConfig),
                    months,
                ),
            );
        } else if (task?.taskType === TaskType.BALANCE_SHEET) {
            const rawBSViewConfig = task.viewConfig ?? null;
            const months =
                readBalanceSheetMonthsFromViewConfig(rawBSViewConfig);
            setViewConfig(
                serializeBalanceSheetCloseTemplateViewConfigWithMonths(
                    parseBalanceSheetCloseTemplateViewConfig(rawBSViewConfig),
                    months,
                ),
            );
        } else if (task?.taskType === TaskType.DOCUMENT_RETRIEVAL) {
            const rawDRViewConfig = task.viewConfig ?? null;
            const accountType =
                readDocumentRetrievalFinancialAccountType(rawDRViewConfig);
            const accountIds =
                readDocumentRetrievalFinancialAccountIds(rawDRViewConfig);
            const startDateOverride =
                readDocumentRetrievalStartDateOverride(rawDRViewConfig);
            const endDateOverride =
                readDocumentRetrievalEndDateOverride(rawDRViewConfig);
            setViewConfig(
                serializeDocumentRetrievalCloseTemplateViewConfigWithDates(
                    parseDocumentRetrievalCloseTemplateViewConfig(
                        rawDRViewConfig,
                    ),
                    {
                        financialAccountType: accountType,
                        financialAccountIds: accountIds,
                        startDateOverride,
                        endDateOverride,
                    },
                ),
            );
        } else if (task?.taskType === TaskType.RECONCILIATION) {
            const rawReconciliationViewConfig = task.viewConfig ?? null;
            const accountTypes = readReconciliationFinancialAccountTypes(
                rawReconciliationViewConfig,
            );
            const accountIds = readReconciliationFinancialAccountIds(
                rawReconciliationViewConfig,
            );
            setViewConfig(
                serializeReconciliationCloseTemplateViewConfig(
                    accountTypes,
                    accountIds,
                ),
            );
        } else if (task?.taskType === TaskType.TRANSACTIONS) {
            const rawTxViewConfig = task.viewConfig ?? null;
            setTransactionsViewType(
                readTransactionsViewTypeFromViewConfig(rawTxViewConfig),
            );
            setViewConfig(rawTxViewConfig);
        } else if (task?.taskType === TaskType.TRANSACTION_REVIEW) {
            setViewConfig(
                normalizeTransactionReviewViewConfigForBasis(
                    task.viewConfig ?? null,
                    basis ?? null,
                ),
            );
        } else {
            setViewConfig(task?.viewConfig ?? null);
        }
    });
    useEffect(() => {
        if (!open) {
            return;
        }
        resetViewConfigFromTask();
    }, [open, task?.localId, task?.taskType]);

    const formik = useFormik<TemplateTaskDetailsValues>({
        enableReinitialize: true,
        initialValues: {
            name: task?.name ?? "",
            closeStageId: task?.closeStageId ?? null,
            assigneeRole: task?.assigneeRole ?? null,
            assigneeId: task?.assigneeId ?? null,
            externalDeadline: task?.externalDeadline ?? null,
            deadlineAnchor: task?.deadlineAnchor ?? null,
            estimatedTime: task?.estimatedTime ?? null,
            initialMessage: task?.initialMessage ?? null,
        },
        validate: validateFormikWithZodSchema(TemplateTaskDetailsSchema),
        onSubmit: async (values) => {
            if (!task) {
                return;
            }
            const rawViewConfig = viewConfigRef.current;
            if (
                taskType === TaskType.TRANSACTION_REVIEW &&
                readReviewFieldsFromViewConfig(rawViewConfig).length === 0
            ) {
                return;
            }

            try {
                await onSave(task.localId, {
                    name: values.name.trim(),
                    closeStageId: values.closeStageId,
                    assigneeRole: values.assigneeRole,
                    assigneeId: values.assigneeId ?? null,
                    externalDeadline: values.externalDeadline,
                    deadlineAnchor: values.deadlineAnchor,
                    estimatedTime: values.estimatedTime,
                    initialMessage: values.initialMessage,
                    viewConfig:
                        taskType === TaskType.TRANSACTIONS
                            ? withTransactionsViewType(
                                  rawViewConfig,
                                  transactionsViewType,
                              )
                            : rawViewConfig,
                    taskType,
                    sourceTemplateVersion,
                    ...(links.isEnabled
                        ? {
                              blockedByLocalIds: links.blockedByLocalIds,
                              relatedLocalIds: links.relatedLocalIds,
                          }
                        : {}),
                });
            } catch {
                return;
            }
        },
    });

    const formikRef = useRef(formik);
    useEffect(() => {
        formikRef.current = formik;
    });

    const handleClose = useCallback(() => {
        formikRef.current.resetForm();
        onClose();
    }, [onClose]);

    const isOutdated =
        latestSourceTemplate !== null &&
        sourceTemplateVersion !== null &&
        latestSourceTemplate.version > sourceTemplateVersion;

    const handleUpdateVersion = useCallback(() => {
        if (!latestSourceTemplate) {
            return;
        }
        const defaults = toDrawerDefaultsFromOrgTaskTemplate({
            template: latestSourceTemplate,
            basis: basis ?? null,
        });
        void formikRef.current.setValues({
            ...defaults.formValues,
            closeStageId:
                defaults.formValues.closeStageId ??
                formikRef.current.values.closeStageId,
        });
        setTaskType(defaults.taskType);
        setViewConfig(defaults.viewConfig);
        setTransactionsViewType(defaults.transactionsViewType);
        setSourceTemplateVersion(latestSourceTemplate.version);
    }, [latestSourceTemplate, basis]);

    const handleDescriptionChange = useInitialMessageHandler(
        formik.setFieldValue,
    );

    const hasMissingReviewFields =
        taskType === TaskType.TRANSACTION_REVIEW &&
        readReviewFieldsFromViewConfig(viewConfig).length === 0;

    return (
        <FormikProvider value={formik}>
            <TemplateTaskDrawerShell
                open={open}
                title="Edit task"
                submitLabel="Save"
                taskType={taskType}
                onClose={handleClose}
                onSubmit={formik.handleSubmit}
                isSubmitDisabled={
                    !formik.values.name.trim() ||
                    hasMissingReviewFields ||
                    formik.isSubmitting
                }
                commandBarStart={
                    isOutdated && (
                        <Button
                            variant="secondary"
                            size="md"
                            type="button"
                            onClick={handleUpdateVersion}
                        >
                            Update version
                        </Button>
                    )
                }
                preview={
                    <TemplateTaskPreviewPane
                        taskType={taskType}
                        previewKey={task?.localId ?? ""}
                        viewConfig={viewConfig}
                        onViewConfigChange={setViewConfig}
                        basis={basis}
                    />
                }
            >
                <TaskDrawerHeaderFields<TemplateTaskDetailsValues>
                    titleName="name"
                    titlePlaceholder="Task name"
                    titleRef={nameInputRef}
                    description={formik.values.initialMessage}
                    onDescriptionChange={handleDescriptionChange}
                    renderWorkflowPicker={
                        taskType === null
                            ? undefined
                            : (pickerProps) => (
                                  <LockedTaskTypePicker
                                      taskType={taskType}
                                      {...pickerProps}
                                  />
                              )
                    }
                />
                <TemplateTaskSections>
                    <TemplateTaskDetailsSection sortedStages={sortedStages} />
                    {links.isEnabled && (
                        <TemplateTaskDependenciesSection>
                            <TaskLinkFields
                                options={links.options}
                                blockedByLocalIds={links.blockedByLocalIds}
                                relatedLocalIds={links.relatedLocalIds}
                                cyclicLocalIds={links.cyclicLocalIds}
                                onBlockedByChange={links.setBlockedByLocalIds}
                                onRelatedChange={links.setRelatedLocalIds}
                            />
                        </TemplateTaskDependenciesSection>
                    )}
                    {taskType !== null && (
                        <TemplateTaskWorkflowSection>
                            <EditTaskWorkflowFields
                                taskType={taskType}
                                viewConfig={viewConfig}
                                onViewConfigChange={setViewConfig}
                                transactionsViewType={transactionsViewType}
                                basis={basis}
                            />
                        </TemplateTaskWorkflowSection>
                    )}
                </TemplateTaskSections>
            </TemplateTaskDrawerShell>
        </FormikProvider>
    );
};
